import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { I18nProvider } from './context/I18nContext';
import { Toaster } from './components/ui/sonner';
import { AppThemeProvider } from './lib/appTheme';
import { SeasonalDecorations } from './components/seasonal/SeasonalDecorations';
import AuthPage from './pages/AuthPage';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import InstallBanner from './components/InstallBanner';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import OfflineIndicator from './components/OfflineIndicator';
import StaffChat from './components/StaffChat';
import OwnerInspectBanner from './components/owner/OwnerInspectBanner';
import { setupAxios } from './lib/axiosSetup';
import '@/App.css';

// One-time axios global setup (401 → /auth, retries, payload-too-large messages)
setupAxios();

// Code splitting per role dashboard — load only what's needed
const GuestDashboard = lazy(() => import('./pages/GuestDashboard'));
const AnimatorDashboard = lazy(() => import('./pages/AnimatorDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const OwnerDashboard = lazy(() => import('./pages/OwnerDashboard'));
const SmartGuide = lazy(() => import('./components/guide/SmartGuide'));
const HodDashboard = lazy(() => import('./pages/HodDashboard'));
const WeeklyProgramPlanner = lazy(() => import('./pages/WeeklyProgramPlanner'));
const CalendarPlanner = lazy(() => import('./pages/CalendarPlanner'));
const SpaPage = lazy(() => import('./pages/SpaPage'));
const FnbHub = lazy(() => import('./pages/FnbHub'));
const FnbVenuePage = lazy(() => import('./pages/FnbVenuePage'));
const FnbOverview = lazy(() => import('./pages/FnbOverview'));
const AccessCodeManager = lazy(() => import('./pages/AccessCodeManager'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const SetupWizard = lazy(() => import('./pages/SetupWizard'));
const RoomRangesPage = lazy(() => import('./pages/RoomRangesPage'));
const PushNotificationHistory = lazy(() => import('./pages/PushNotificationHistory'));
const ChiefGamesPage = lazy(() => import('./pages/ChiefGamesPage'));
const ChiefKidsFamilyPage = lazy(() => import('./pages/ChiefKidsFamilyPage'));
const ChiefKidsFamilyHodDashboard = lazy(() => import('./pages/kids-family/ChiefKidsFamilyHodDashboard'));
const ChiefKidsFamilyHodSettings = lazy(() => import('./pages/kids-family/ChiefKidsFamilyHodSettings'));
const ChiefKidsFamilyHodChildren = lazy(() => import('./pages/kids-family/ChiefKidsFamilyHodChildren'));
const ChiefKidsFamilyHodHistory = lazy(() => import('./pages/kids-family/ChiefKidsFamilyHodHistory'));
const ChiefKidsFamilyHodFeedback = lazy(() => import('./pages/kids-family/ChiefKidsFamilyHodFeedback'));
const ChiefKidsFamilyHodBabysitting = lazy(() => import('./pages/kids-family/ChiefKidsFamilyHodBabysitting'));
// Tablet ops — kids_club_ops role only
const TabletPages = lazy(() => import('./pages/kids-family/TabletPages'));
const TabletDashboardPage = lazy(() => import('./pages/kids-family/TabletPages').then(m => ({ default: m.TabletDashboard })));
const TabletCheckInPage = lazy(() => import('./pages/kids-family/TabletPages').then(m => ({ default: m.TabletCheckIn })));
const TabletCheckOutPage = lazy(() => import('./pages/kids-family/TabletPages').then(m => ({ default: m.TabletCheckOut })));
const TabletWalkInPage = lazy(() => import('./pages/kids-family/TabletPages').then(m => ({ default: m.TabletWalkIn })));
const TabletLivePage = lazy(() => import('./pages/kids-family/TabletPages').then(m => ({ default: m.TabletLive })));
const TabletExpectedPage = lazy(() => import('./pages/kids-family/TabletPages').then(m => ({ default: m.TabletExpected })));
const ChiefKidsFamilyHodConversations = lazy(() => import('./pages/kids-family/ChiefKidsFamilyHodConversations'));
const ChiefKidsFamilyHodTabletAccount = lazy(() => import('./pages/kids-family/ChiefKidsFamilyHodTabletAccount'));
// Parent / Guest Kids & Family surface (mounts under /guest/kids-family)
const ParentHome = lazy(() => import('./pages/kids-family/parent/ParentHome'));
const ParentRegisterChild = lazy(() => import('./pages/kids-family/parent/RegisterChild'));
const ParentBookKidsClub = lazy(() => import('./pages/kids-family/parent/BookKidsClub'));
const ParentMessages = lazy(() => import('./pages/kids-family/parent/ParentMessages'));
const ParentFeedback = lazy(() => import('./pages/kids-family/parent/ParentFeedback'));
const ParentBabysittingHome = lazy(() => import('./pages/kids-family/parent/BabysittingHome'));
const ParentBabysittingRequest = lazy(() => import('./pages/kids-family/parent/BabysittingRequest'));
const ParentBabysittingRequestDetail = lazy(() => import('./pages/kids-family/parent/BabysittingRequestDetail'));
const ParentBabysittingFeedback = lazy(() => import('./pages/kids-family/parent/BabysittingFeedback'));
const MysteriousPhraseGame = lazy(() => import('./games/mysterious-phrase/MysteriousPhraseGame'));

// Hidden Music Store sub-module — admin / chief / limited_editor only
const MusicStoreDashboard = lazy(() => import('./modules/music-store/pages/StoreDashboard'));
const MusicStoreCategories = lazy(() => import('./modules/music-store/pages/CategoriesPage'));
const MusicStoreOrders = lazy(() => import('./modules/music-store/pages/MusicStoreOrdersPage'));

// Public-facing pages (no auth required)
const StorePage = lazy(() => import('./pages/StorePage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const ExploreHotelSelect = lazy(() => import('./pages/ExploreHotelSelect'));
const CheckinPage = lazy(() => import('./pages/CheckinPage'));
const OrderReceiptPage = lazy(() => import('./pages/OrderReceiptPage'));

// Bingo internal module (Feb 2026) — lazy-loaded so the ~180 kB Bingo
// bundle doesn't bloat FunFlow's initial payload for guests who never open
// it. Structural mount only — no dashboard link yet; access is direct-URL
// only until admin enables the feature in the next phase.
const BingoHostPage = lazy(() => import('./plugins/bingo/host/HostPanel'));
const BingoProjectorPage = lazy(() => import('./plugins/bingo/projector/ProjectorView'));
const BingoSettingsPage = lazy(() => import('./core/settings/Settings'));

// Bingo's own i18n dictionary (localises the tab labels + Settings header).
// Kept separate from FunFlow's I18nContext so we don't force one to know
// about the other — we mount Bingo's provider only around the Bingo
// Settings route.
import { I18nProvider as BingoI18nProvider } from './core/i18n/I18nProvider';

// Register the Bingo plugin at bootstrap so `listPlugins()` returns it
// and the shared Settings page renders the "Bingo Show" tab. Kept out of
// the lazy chunks on purpose — the registry entry is tiny and needs to
// exist before the Settings route mounts.
import { registerPlugin } from './core/registry/pluginRegistry';
import BingoPlugin from './plugins/bingo';
import KaraokeDashboard from './pages/karaoke/KaraokeDashboard';
import KaraokeProjector from './pages/karaoke/KaraokeProjector';
import KaraokeRequest from './pages/karaoke/KaraokeRequest';
const IQHome = lazy(() => import('./icquiz/pages/Home'));
const IQPlayerJoin = lazy(() => import('./icquiz/pages/PlayerJoin'));
const IQPlayerGame = lazy(() => import('./icquiz/pages/PlayerGame'));
const IQMillionairePublic = lazy(() => import('./icquiz/pages/MillionairePublic'));
const IQQuizAdmin = lazy(() => import('./icquiz/pages/QuizAdmin'));
const IQQuizEditor = lazy(() => import('./icquiz/pages/QuizEditor'));
const IQQuizPreview = lazy(() => import('./icquiz/pages/QuizPreview'));
const IQHostSetup = lazy(() => import('./icquiz/pages/HostSetup'));
const IQHostGame = lazy(() => import('./icquiz/pages/HostGame'));
const IQMillionaireLibrary = lazy(() => import('./icquiz/pages/MillionaireLibrary'));
const IQMillionaireSetup = lazy(() => import('./icquiz/pages/MillionaireSetup'));
const IQMillionaireOperator = lazy(() => import('./icquiz/pages/MillionaireOperator'));
const IQMillionaireSessions = lazy(() => import('./icquiz/pages/MillionaireSessions'));
const IQResumeLink = lazy(() => import('./icquiz/pages/ResumeLink'));
const IQImportQuiz = lazy(() => import('./icquiz/pages/ImportQuiz'));
const IQVerifyClaim = lazy(() => import('./icquiz/pages/VerifyClaim'));
const IQAutoScreen = lazy(() => import('./icquiz/pages/AutoScreen'));
const IQQuizTranslations = lazy(() => import('./icquiz/pages/QuizTranslations'));
const IQ_STAFF = ['chief_animator'];
registerPlugin(BingoPlugin);

const PageLoader = () => (
  <div
    style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 20,
      background: 'linear-gradient(135deg,#0e7460 0%,#e8b85f 100%)',
      color: '#fff',
      fontFamily: 'system-ui,-apple-system,sans-serif',
    }}
    data-testid="app-loader"
  >
    <div
      style={{
        width: 72, height: 72, borderRadius: 18,
        background: 'rgba(255,255,255,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(8px)',
        animation: 'ic-loader-pulse 1.4s ease-in-out infinite',
      }}
    >
      <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
        <path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
        <path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      </svg>
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 0.5 }}>IC FunFlow</div>
    <div style={{ fontSize: 13, opacity: 0.85 }}>Loading your dashboard…</div>
    <style>{`@keyframes ic-loader-pulse { 0%,100% { transform: scale(1); opacity: 1 } 50% { transform: scale(1.08); opacity: 0.7 } }`}</style>
  </div>
);

// Role constants to avoid inline object creation
const ROLES = {
  ADMIN_DASHBOARD: ['admin', 'limited_editor', 'chief_animator'],
  ANIMATOR_DASHBOARD: ['animator', 'admin', 'chief_animator'],
  ADMIN_VIEW_ANIMATOR: ['admin', 'chief_animator'],
  GUEST_DASHBOARD: ['guest', 'admin', 'animator', 'chief_animator', 'limited_editor'],
  WEEKLY_PLANNER: ['admin', 'limited_editor', 'chief_animator'],
  CALENDAR_PLANNER: ['admin', 'limited_editor', 'chief_animator'],
  ACCESS_CODES: ['owner', 'admin', 'chief_animator'],
  SETUP_WIZARD: ['owner', 'admin'],
  // Feb 2026 — Owner is platform-level; deliberately a separate lane from
  // hotel-scoped admin (see backend `auth.get_owner_user`). Do NOT add
  // 'owner' to any of the admin/animator/guest role lists above.
  OWNER_DASHBOARD: ['owner'],
  // Feb 2026 · Generic HOD (all non-Entertainment / non-Kids
  // departments). Each department stays isolated via `user.department`.
  HOD_DASHBOARD: ['hod'],
};

// Register Service Worker. We avoid auto-reloading on controllerchange to
// prevent reload loops; the ChunkErrorBoundary handles broken chunks safely.
const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    });
    console.log('ServiceWorker registered:', registration.scope);

    // Activate a waiting worker exactly once per session (avoid reload loops)
    if (registration.waiting && !sessionStorage.getItem('ic_sw_skipwaiting')) {
      sessionStorage.setItem('ic_sw_skipwaiting', '1');
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New version installed; activate it but DO NOT reload here.
          // Next manual navigation will pick up the new build naturally.
          if (!sessionStorage.getItem('ic_sw_skipwaiting')) {
            sessionStorage.setItem('ic_sw_skipwaiting', '1');
            newWorker.postMessage({ type: 'SKIP_WAITING' });
          }
        }
      });
    });
  } catch (error) {
    console.error('ServiceWorker registration failed:', error);
  }
};

// Feb 2026 — Task 3: honour ?next=/checkin (or other public path) after
// login so QR-first guest activation lands back on the checkin flow.
const AuthNextRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const next = params.get('next');
  // Only honour whitelisted public prefixes to prevent open-redirects.
  const safeNext = next && /^\/(checkin|explore|guest|admin|animator|owner)(\/|\?|$)/.test(next) ? next : '/';
  return <Navigate to={safeNext} replace />;
};

// Full-screen theatre displays must stay clean: no decorations, install prompts or chat bubbles.
const NotOnStageScreens = ({ children }) => {
  const { pathname } = useLocation();
  return pathname.startsWith('/karaoke/projector') ? null : children;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Feb 23 2026 · Owner "Enter Hotel" inspection.
    // When the Owner has entered a hotel from the Owner dashboard we
    // stash the hotel id in sessionStorage. In that mode we admit the
    // Owner to admin-scoped routes so they can inspect the hotel's
    // dashboards. Backend enforces read-only via GET-only stamping.
    let inspecting = false;
    try {
      inspecting = !!sessionStorage.getItem('owner_inspect_hotel_id');
    } catch (_) { /* ignore */ }
    if (user.role === 'owner' && inspecting) {
      return children;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

const ROLE_THEME = {
  owner: 'admin', admin: 'admin', limited_editor: 'admin', hod: 'admin', fnb_supervisor: 'admin',
  chief_animator: 'chief', animator: 'staff', kids_club_ops: 'staff',
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  // Role-tinted visual identity (CSS tokens in index.css) — purely cosmetic.
  useEffect(() => {
    const theme = user ? (user.is_full_admin ? 'admin' : (ROLE_THEME[user.role] || 'guest')) : 'guest';
    document.body.dataset.roleTheme = theme;
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route
        path="/auth"
        element={user ? <AuthNextRedirect /> : <AuthPage />}
      />

      {/* Public — no authentication required */}
      <Route path="/store" element={<StorePage />} />
      <Route path="/store/order/:order_number" element={<OrderReceiptPage />} />
      {/* Feb 2026 — Task 3: QR-first guest activation (`/checkin?h=…&v=…`). */}
      <Route path="/checkin" element={<CheckinPage />} />
      {/* Bingo internal module (Feb 2026). Two entry points:
          • Host: staff-only control panel at /host/bingo (Bingo's original)
          • Projector: public read-only big-screen at /projector/bingo
          Route paths MATCH Bingo's internal `window.open('/projector/bingo')`
          hand-off so the projector opens correctly instead of bouncing
          back to FunFlow's admin dashboard. */}
      <Route
        path="/host/bingo"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator']}>
            <BingoHostPage />
          </ProtectedRoute>
        }
      />
      {/* Backwards-compatible legacy alias — keeps the Admin tile working. */}
      <Route
        path="/admin/bingo/host"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator']}>
            <BingoHostPage />
          </ProtectedRoute>
        }
      />
      <Route path="/projector/bingo" element={<BingoProjectorPage />} />
      {/* IC.Karaoke — staff dashboard + projector (team-gated by the API), public guest page via hotel QR */}
      <Route path="/karaoke" element={<ProtectedRoute><KaraokeDashboard /></ProtectedRoute>} />
      <Route path="/karaoke/projector" element={<ProtectedRoute><KaraokeProjector /></ProtectedRoute>} />
      <Route path="/karaoke/r/:code" element={<KaraokeRequest />} />
      {/* IC.Quiz — players join publicly by PIN/QR; hosting is Chief Animator only */}
      <Route path="/icquiz/play" element={<IQPlayerJoin />} />
      <Route path="/icquiz/play/:sessionId" element={<IQPlayerGame />} />
      <Route path="/icquiz/r/:token" element={<IQResumeLink />} />
      <Route path="/icquiz/screen/:sessionId" element={<IQAutoScreen />} />
      <Route path="/icquiz/millionaire/public/:sessionId" element={<IQMillionairePublic />} />
      <Route path="/icquiz" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQHome /></ProtectedRoute>} />
      <Route path="/icquiz/admin/import" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQImportQuiz /></ProtectedRoute>} />
      <Route path="/icquiz/verify" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQVerifyClaim /></ProtectedRoute>} />
      <Route path="/icquiz/verify/:token" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQVerifyClaim /></ProtectedRoute>} />
      <Route path="/icquiz/admin" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQQuizAdmin /></ProtectedRoute>} />
      <Route path="/icquiz/admin/quiz/:id" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQQuizEditor /></ProtectedRoute>} />
      <Route path="/icquiz/admin/quiz/:id/translations" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQQuizTranslations /></ProtectedRoute>} />
      <Route path="/icquiz/admin/quiz/:id/preview" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQQuizPreview /></ProtectedRoute>} />
      <Route path="/icquiz/host" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQHostSetup /></ProtectedRoute>} />
      <Route path="/icquiz/host/:sessionId" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQHostGame /></ProtectedRoute>} />
      <Route path="/icquiz/millionaire" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQMillionaireLibrary /></ProtectedRoute>} />
      <Route path="/icquiz/millionaire/setup/:id" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQMillionaireSetup /></ProtectedRoute>} />
      <Route path="/icquiz/millionaire/run/:sessionId" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQMillionaireOperator /></ProtectedRoute>} />
      <Route path="/icquiz/millionaire/sessions" element={<ProtectedRoute allowedRoles={IQ_STAFF}><IQMillionaireSessions /></ProtectedRoute>} />
      {/* Legacy alias for the previous URL — keeps any bookmarks working. */}
      <Route path="/bingo/projector" element={<BingoProjectorPage />} />
      {/* Bingo Settings — scoped under /host/bingo so the Bingo's own
          `/settings` link doesn't collide with future FunFlow routes.
          Admin-only, same gating as the Host Panel. The page itself is
          the ORIGINAL Bingo Settings shell (4 core tabs + Bingo plugin
          tab) — nothing was redesigned. */}
      <Route
        path="/host/bingo/settings"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator']}>
            <BingoI18nProvider>
              <BingoSettingsPage />
            </BingoI18nProvider>
          </ProtectedRoute>
        }
      />
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/explore/select" element={<ExploreHotelSelect />} />

      {/* Chief Games — central management hub for all IC FunFlow games.
          First step of the Chief games directory. Individual game
          modules (Bingo, Lucky Number, IC Quiz, etc.) are unchanged;
          this page only links out to them. */}
      <Route
        path="/chief/games"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator']}>
            <ChiefGamesPage />
          </ProtectedRoute>
        }
      />

      {/* Kids & Family — Chief Animator dashboard entry point for the
          "Kids & Family" hotel department (relocated from the Admin
          Departments grid in Feb 2026). Same underlying data, Chief
          role/session, Chief-scoped Back → /animator. Full operational
          Kids Club module lands here in the next step. */}
      {/* Kids & Family — Chief Animator entry. Since the full
          IC.KidsClub port landed, the intermediate "hub" tile page has
          been retired: `/chief/kids-family` now renders the HOD
          Dashboard directly. The legacy `/chief/kids-family/hod` path
          is preserved as an alias so any bookmark still works. */}
      <Route
        path="/chief/kids-family"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator']}>
            <ChiefKidsFamilyHodDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chief/kids-family/hod"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator']}>
            <ChiefKidsFamilyHodDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/chief/kids-family/hod/settings" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator']}><ChiefKidsFamilyHodSettings /></ProtectedRoute>} />
      <Route path="/chief/kids-family/hod/children" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator']}><ChiefKidsFamilyHodChildren /></ProtectedRoute>} />
      <Route path="/chief/kids-family/hod/history" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator']}><ChiefKidsFamilyHodHistory /></ProtectedRoute>} />
      <Route path="/chief/kids-family/hod/feedback" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator']}><ChiefKidsFamilyHodFeedback /></ProtectedRoute>} />
      <Route path="/chief/kids-family/hod/babysitting" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator']}><ChiefKidsFamilyHodBabysitting /></ProtectedRoute>} />
      <Route path="/chief/kids-family/hod/conversations" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator']}><ChiefKidsFamilyHodConversations /></ProtectedRoute>} />
      {/* Rules Editor — reuses the existing Settings page (rules tab) so
          the two stay in sync automatically. */}
      <Route path="/chief/kids-family/hod/rules" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator']}><ChiefKidsFamilyHodSettings /></ProtectedRoute>} />
      {/* Kids Club Tablet Account management — Admin + Kids & Family HOD.
          Route is under the chief namespace but ProtectedRoute admits
          the Hotel Admin role too, so the same page serves both. */}
      <Route path="/chief/kids-family/hod/tablet-account" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator']}><ChiefKidsFamilyHodTabletAccount /></ProtectedRoute>} />

      {/* Kids Club Tablet — kids_club_ops role only (limited device account).
          Chief Animator can also open these for setup/testing. */}
      <Route path="/tablet" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'kids_club_ops']}><TabletDashboardPage /></ProtectedRoute>} />
      <Route path="/tablet/checkin" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'kids_club_ops']}><TabletCheckInPage /></ProtectedRoute>} />
      <Route path="/tablet/checkout" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'kids_club_ops']}><TabletCheckOutPage /></ProtectedRoute>} />
      <Route path="/tablet/walkin" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'kids_club_ops']}><TabletWalkInPage /></ProtectedRoute>} />
      <Route path="/tablet/live" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'kids_club_ops']}><TabletLivePage /></ProtectedRoute>} />
      <Route path="/tablet/expected" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'kids_club_ops']}><TabletExpectedPage /></ProtectedRoute>} />

      {/* ── Parent / Guest Kids & Family surface ─────────────────
          Reuses IC FunFlow's Guest JWT. All /guest/kids-family/* routes
          are strictly Guest-context — never routed to /admin, /chief
          or /animator. Chief/Admin can also open these routes for
          testing so the same URL demoable end-to-end, but the pages
          themselves scope all data to `owner_user_id = current user`. */}
      <Route path="/guest/kids-family" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'guest']}><ParentHome /></ProtectedRoute>} />
      <Route path="/guest/kids-family/register" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'guest']}><ParentRegisterChild /></ProtectedRoute>} />
      <Route path="/guest/kids-family/book" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'guest']}><ParentBookKidsClub /></ProtectedRoute>} />
      <Route path="/guest/kids-family/messages" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'guest']}><ParentMessages /></ProtectedRoute>} />
      <Route path="/guest/kids-family/feedback" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'guest']}><ParentFeedback /></ProtectedRoute>} />
      <Route path="/guest/kids-family/babysitting" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'guest']}><ParentBabysittingHome /></ProtectedRoute>} />
      <Route path="/guest/kids-family/babysitting/new" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'guest']}><ParentBabysittingRequest /></ProtectedRoute>} />
      <Route path="/guest/kids-family/babysitting/view/:id" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'guest']}><ParentBabysittingRequestDetail /></ProtectedRoute>} />
      <Route path="/guest/kids-family/babysitting/feedback/:id" element={<ProtectedRoute allowedRoles={['admin', 'chief_animator', 'guest']}><ParentBabysittingFeedback /></ProtectedRoute>} />

      {/* Mysterious Phrase — game module imported from
          github.com/Marnad1985/the-mysterious-phrase in Feb 2026.
          Logic, wheel and phrase-reveal flow are preserved verbatim;
          only routing + auth are IC FunFlow's. Reached from the
          Games hub at /chief/games. */}
      <Route
        path="/host/mysterious-phrase"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator']}>
            <MysteriousPhraseGame />
          </ProtectedRoute>
        }
      />

      <Route
        path="/"
        element={
          user ? (
            <ProtectedRoute>
              {(() => {
                // Feb 23 2026 · Owner in "Enter Hotel" inspection mode
                // must land on AdminDashboard so they can inspect the
                // hotel. Otherwise the Owner sees OwnerDashboard as usual.
                let inspecting = false;
                try {
                  inspecting = !!sessionStorage.getItem('owner_inspect_hotel_id');
                } catch (_) { /* ignore */ }
                if (user?.role === 'owner') {
                  return inspecting ? <AdminDashboard /> : <OwnerDashboard />;
                }
                if (user?.role === 'admin') return <AdminDashboard />;
                if (user?.role === 'animator') return <AnimatorDashboard />;
                if (user?.role === 'chief_animator') {
                  return user?.is_full_admin ? <AdminDashboard /> : <AnimatorDashboard />;
                }
                if (user?.role === 'limited_editor') return <AdminDashboard />;
                if (user?.role === 'kids_club_ops') return <Navigate to="/tablet" replace />;
                if (user?.role === 'hod') return <HodDashboard />;
                if (user?.role === 'fnb_supervisor') return <Navigate to="/fnb/overview" replace />;
                return <GuestDashboard />;
              })()}
            </ProtectedRoute>
          ) : (
            <WelcomePage />
          )
        }
      />

      <Route
        path="/owner"
        element={
          <ProtectedRoute allowedRoles={ROLES.OWNER_DASHBOARD}>
            <OwnerDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={ROLES.ADMIN_DASHBOARD}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/animator"
        element={
          <ProtectedRoute allowedRoles={ROLES.ANIMATOR_DASHBOARD}>
            <AnimatorDashboard />
          </ProtectedRoute>
        }
      />

      {/* Admin viewing specific animator's dashboard */}
      <Route
        path="/animator/:animatorId"
        element={
          <ProtectedRoute allowedRoles={ROLES.ADMIN_VIEW_ANIMATOR}>
            <AnimatorDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/hod"
        element={
          <ProtectedRoute allowedRoles={ROLES.HOD_DASHBOARD}>
            <HodDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/guest"
        element={
          <ProtectedRoute allowedRoles={ROLES.GUEST_DASHBOARD}>
            <GuestDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/weekly-planner"
        element={
          <ProtectedRoute allowedRoles={ROLES.WEEKLY_PLANNER}>
            <WeeklyProgramPlanner />
          </ProtectedRoute>
        }
      />

      <Route
        path="/spa"
        element={
          <ProtectedRoute>
            <SpaPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/fnb"
        element={
          <ProtectedRoute>
            <FnbHub />
          </ProtectedRoute>
        }
      />

      <Route
        path="/fnb/overview"
        element={
          <ProtectedRoute>
            <FnbOverview />
          </ProtectedRoute>
        }
      />

      <Route
        path="/fnb/:venueId"
        element={
          <ProtectedRoute>
            <FnbVenuePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/calendar-planner"
        element={
          <ProtectedRoute allowedRoles={ROLES.CALENDAR_PLANNER}>
            <CalendarPlanner />
          </ProtectedRoute>
        }
      />

      <Route
        path="/access-codes"
        element={
          <ProtectedRoute allowedRoles={ROLES.ACCESS_CODES}>
            <AccessCodeManager />
          </ProtectedRoute>
        }
      />

      {/* Public Team Page - No auth required */}
      <Route path="/team" element={<TeamPage />} />

      {/* Production Setup Wizard — admin only (route also gated client-side
          to avoid even rendering the page for non-admins; the backend
          endpoints additionally require ADMIN_SEED_TOKEN for any destructive
          operation). */}
      <Route
        path="/setup"
        element={
          <ProtectedRoute allowedRoles={ROLES.SETUP_WIZARD}>
            <SetupWizard />
          </ProtectedRoute>
        }
      />

      {/* Room Ranges configuration — Admin / Chief Animator only */}
      <Route
        path="/room-ranges"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator']}>
            <RoomRangesPage />
          </ProtectedRoute>
        }
      />

      {/* Push Notification History — Admin only. Audits every broadcast push
          sent (manual + scheduler) with delivery stats. */}
      <Route
        path="/admin/push-history"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <PushNotificationHistory />
          </ProtectedRoute>
        }
      />

      {/* Hidden Music Store — Admin / Chief / Limited Editor only.
          The backend also gates every endpoint via require_store_manager,
          so even if someone reaches the URL we're safe. */}
      <Route
        path="/music-store"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator', 'limited_editor']}>
            <MusicStoreDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/music-store/categories"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator', 'limited_editor']}>
            <MusicStoreCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/music-store/orders"
        element={
          <ProtectedRoute allowedRoles={['admin', 'chief_animator', 'limited_editor']}>
            <MusicStoreOrders />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
};

function App() {
  useEffect(() => {
    // Register service worker on app load
    registerServiceWorker();
  }, []);

  return (
    <I18nProvider>
    <AuthProvider>
      <AppThemeProvider>
      <BrowserRouter>
        <div className="App">
          <OwnerInspectBanner />
          <OfflineIndicator />
          <AppRoutes />
          <Suspense fallback={null}><SmartGuide /></Suspense>
          <NotOnStageScreens>
            <SeasonalDecorations />
            <PWAInstallPrompt />
            <InstallBanner />
            <StaffChat />
          </NotOnStageScreens>
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
      </AppThemeProvider>
    </AuthProvider>
    </I18nProvider>
  );
}

export default App;
